/**
 * The outsourcing pallet checklists as flows (2026-10-26; jobq-api main.py "PALLET HANDOFFS"), shared by the
 * coating desk (CoatingManagementPage) and the floor's Receive from vendor (JobQueuePage):
 *
 *   sendWithChecklist   Send a manifest: first the paper is re-checked against what the packing plan packs
 *                       (a difference shows and offers "Fix the paper and send"), then, when its pallets can
 *                       be checked, the checklist (every pallet: beacon pulled + docked on the shipping shelf,
 *                       4x6 label on); the send and the record are ONE request (POST /send with pallet_checklist).
 *   offerReattach       After a receive: the pallets now back with a beacon to put on. Nothing back = no
 *                       popup.
 *   showPallets         The list for finding only (Find all, the map), nothing recorded.
 *
 * Every call that can't reach JobQ falls back to the old behaviour (a send without the checklist, no
 * reattach popup): the checklist never stands between the office and a paper.
 */
import pinPrompt from '../../utils/pinPrompt';
import palletChecklistPrompt from '../../components/PalletChecklistModal';
import paperDiffPrompt from './paperDiffPrompt';
import { loadOpoChecklist, reattachPending, recheckOpo, recordOpoChecklist } from './palletChecklist';

/** {ok, data} | {ok:false, message, differs?}: POST /outside_processing_orders/{id}/send (never throws). A Draft
 *  that no longer matches what the packing plan packs is a 409 {detail: {code: 'paper_differs', message, ...}}
 *  (jobq-api 2026-10-29): `differs` = that detail, `message` its sentence. */
export async function postOpoSend(apiBase, opoId, body, parseError) {
  try {
    const res = await fetch(`${apiBase}/outside_processing_orders/${opoId}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let detail = null;
      try { detail = JSON.parse(text).detail; } catch (_) { detail = null; }
      if (detail && typeof detail === 'object' && ['paper_differs', 'paper_changed'].includes(detail.code)) {
        return { ok: false, message: detail.message, differs: detail };
      }
      const replay = { status: res.status, text: async () => text };
      return { ok: false, message: parseError ? await parseError(replay) : `HTTP ${res.status}` };
    }
    return { ok: true, data: await res.json() };
  } catch (err) {
    return { ok: false, message: (err && err.message) || 'Failed to send the manifest.' };
  }
}

/** A manager sends the paper as drafted (jobq-api logs it, with the differences): the send body's override
 *  fields, or null when the PIN or the reason (5 characters or more) was not given. */
async function managerOverride(opo, askReason) {
  const pin = await pinPrompt(`Send ${opo.opo_number} as drafted: a manager's PIN:`, { autofill: false });
  if (!pin) return null;
  const why = await askReason(`Why send ${opo.opo_number} as drafted, not matching its pallets? (5 characters or more)`);
  if (!why || why.trim().length < 5) return null;
  return { recheck_override_pin: pin, recheck_override_reason: why.trim() };
}

/**
 * A paper that differs from what the packing plan packs: show it (`askFix`) and do what was picked. Resolves
 * {go: true, extra, fixed} to carry on (`extra` = what every send body must carry: the manager's override, or on a
 * send's own 409 the fix riding on the send; `fixed` = the fix's answer when the paper was rewritten here), or
 * {go: false, message?}. The fix carries the totals the person was shown (`expect`): if the paper changed since,
 * jobq-api answers 409 paper_changed and the new differences are shown again.
 */
async function resolveDifferences(opo, check, { askFix, askReason, identity, alert, onSend = false }) {
  let shown = check;
  for (let i = 0; i < 3; i += 1) {
    const choice = await askFix(shown, opo.opo_number);
    const pick = choice === true ? 'fix' : choice;
    if (pick === 'asis') {
      const extra = await managerOverride(opo, askReason);
      return extra ? { go: true, extra, fixed: null } : { go: false };
    }
    if (pick !== 'fix' || shown.fixable === false) return { go: false };
    if (onSend) return { go: true, extra: { fix_lines: true, fix_expect: shown.fixed }, fixed: null };
    const who = identity ? await identity() : {};
    if (!who) return { go: false };
    const fx = await recheckOpo(opo.opo_id, { fix: true, pin: who.pin || null, expect: shown.fixed });
    if (fx.ok && fx.body && (fx.body.applied || !fx.body.differs)) {
      return { go: true, extra: {}, fixed: fx.body.applied ? fx.body : null };
    }
    const detail = fx.body && fx.body.detail;
    if (fx.status === 409 && detail && typeof detail === 'object' && detail.code === 'paper_changed') {
      shown = detail;
      continue;
    }
    const why = String(fx.message || (fx.body && fx.body.message) || 'try again').replace(/[.\s]+$/, '');
    const message = `${opo.opo_number} was not fixed: ${why}. Nothing was sent.`;
    if (alert) await alert(message);
    return { go: false, message };
  }
  return { go: false };
}

/**
 * The Send re-check before anything is asked (jobq-api 2026-10-29: KC-SVC-2026-0027 was drafted with each job's
 * full quantity and without job 10136's 62 riders). A re-check that can't be read (an older API, no answer) carries
 * on: Send itself refuses a paper that differs.
 */
async function recheckBeforeSend(opo, opts) {
  const rc = await recheckOpo(opo.opo_id);
  if (!rc.ok || !rc.body || !rc.body.differs) return { go: true, extra: {}, fixed: null };
  return resolveDifferences(opo, rc.body, opts);
}

/**
 * Send a manifest. Resolves {sent: true, data} once the paper is Sent, {sent: false} when the person
 * stopped. `confirmPlain(text)` asks the old "Send …?" question when there is no checklist (window.confirm
 * on the desktop; a phone's panel: both are awaited); `alert(text)` shows a refusal outside the checklist.
 *
 * The Shipping hub (Phase 1, 2026-10-27) also passes:
 *   pin(message)      who is sending: the Working-as session's token (else a typed PIN, autofill off, as before).
 *                     When given, every send carries it as `pin` (jobq-api records who sent the paper).
 *   strict            the checklist must be read: a failed read stops the send ({sent:false, blocked:true,
 *                     message}) instead of falling back to the plain confirm.
 *   allowUnchecked(message)  strict only: asked after a failed read; true = send without the checklist (the hub
 *                     offers it only once Office tools are unlocked). Absent = never.
 * The coating desk passes none of these and behaves as before (a failed read = the plain confirm).
 *
 * Both pass, for the re-check (2026-10-29):
 *   identity()        who fixes a paper that differs: {pin} (the hub's Working-as), {} (the desk: nobody), null = stop.
 *   askFix(check, n)  shows the differences: 'fix' (or true) = "Fix the paper and send", 'asis' = a manager sends it
 *                     as drafted (a manager's PIN + a reason, `askReason`), anything else stops (default: paperDiffPrompt).
 * Every answer carries `fixed` (the fix's answer, or null): a paper fixed and then not sent is still changed.
 */
export async function sendWithChecklist({ apiBase, opo, parseError, confirmPlain, alert, pin: askPin = null,
  strict = false, allowUnchecked = null, identity = null, askFix = paperDiffPrompt,
  // the desktop's default; a phone passes its own (ui.prompt): phone screens never call window.prompt
  askReason = (text) => window.prompt(text) }) {
  const vendorLabel = opo.vendor_name || 'the vendor';
  const injected = typeof askPin === 'function';
  const getPin = injected ? askPin : (message) => pinPrompt(message, { autofill: false });
  const actor = (p) => (injected ? { pin: p } : {});
  const ask = { askFix, askReason, identity, alert };
  const pre = await recheckBeforeSend(opo, ask);
  const fixed = pre.fixed || null;
  if (!pre.go) return { sent: false, fixed, ...(pre.message ? { message: pre.message } : {}) };
  const extra = pre.extra || {};
  // the send; a paper that changed since the check (409 paper_differs) asks again, the fix or override riding on it
  const send = async (body) => {
    let r = await postOpoSend(apiBase, opo.opo_id, { ...extra, ...body }, parseError);
    for (let i = 0; i < 2 && !r.ok && r.differs; i += 1) {
      const res = await resolveDifferences(opo, r.differs, { ...ask, onSend: true });
      if (!res.go) return { cancelled: true, stopped: true, message: res.message };
      r = await postOpoSend(apiBase, opo.opo_id, { ...extra, ...body, ...res.extra }, parseError);
    }
    return r;
  };
  const cl = await loadOpoChecklist(opo.opo_id, 'to_vendor');
  if (strict && !cl.ok) {
    const message = `Couldn't read the pallet checklist for ${opo.opo_number} (${cl.message || 'no answer'}). `
      + 'Nothing was sent: every pallet is checked off before it leaves. Try again, or ask the office.';
    const go = allowUnchecked ? await allowUnchecked(message) : false;
    if (!go) {
      if (alert) await alert(message);
      return { sent: false, blocked: true, message, fixed };
    }
  }
  const pallets = cl.ok && cl.body && Array.isArray(cl.body.pallets) ? cl.body.pallets : [];
  if (pallets.length && cl.body.recording) {
    const out = await palletChecklistPrompt({
      opoId: opo.opo_id,
      step: 'to_vendor',
      initial: cl.body,
      requireAll: true,
      recordLabel: `Record & send to ${vendorLabel}`,
      onSubmit: async (items) => {
        // every pallet already recorded (a paper set back to Draft): just send it again
        if (!items.length) {
          if (!injected) return send({});
          const p = await getPin(`Sending ${opo.opo_number}: enter your PIN:`);
          if (!p) return { cancelled: true };
          return send(actor(p));
        }
        // the person who checked the pallets, typed every time (an admin session must not stand in for them)
        const p = await getPin(`Sending ${opo.opo_number}: enter your PIN:`);
        if (!p) return { cancelled: true };
        return send({ pallet_checklist: { pin: p, items }, ...actor(p) });
      },
    });
    if (!out || !out.recorded) return { sent: false, fixed };
    return { sent: true, data: out.result.data, fixed };
  }
  if (!(await confirmPlain(`Send ${opo.opo_number} to ${vendorLabel}? This prints the manifest.`))) return { sent: false, fixed };
  let body = {};
  if (injected) {
    const p = await getPin(`Sending ${opo.opo_number}: enter your PIN:`);
    if (!p) return { sent: false, fixed };
    body = actor(p);
  }
  const r = await send(body);
  if (r.stopped) return { sent: false, fixed };
  if (!r.ok) {
    await alert(r.message);
    return { sent: false, fixed };
  }
  return { sent: true, data: r.data, fixed };
}

/** After a receive: put the beacons back on the pallets that are back. Resolves {shown, recorded, message}.
 *  `always` opens the list even with nothing to put back (the At Vendor tab's button); `message` says why
 *  nothing opened when the list couldn't be read. A server that can't record yet shows the list to find only.
 *  `pin(message)` (the Shipping hub): who records it, the Working-as session's token; else a typed PIN. */
export async function offerReattach(opoId, { always = false, pin: askPin = null } = {}) {
  if (!opoId) return { shown: false, recorded: false };
  const cl = await loadOpoChecklist(opoId, 'from_vendor');
  if (!cl.ok || !cl.body) return { shown: false, recorded: false, message: cl.message || 'Could not load the pallets.' };
  if (!always && !reattachPending(cl.body).length) return { shown: false, recorded: false };
  const out = await palletChecklistPrompt({
    opoId,
    step: 'from_vendor',
    initial: cl.body,
    recordLabel: 'Record beacons back on',
    onSubmit: !cl.body.recording ? null : async (items) => {
      const pin = typeof askPin === 'function'
        ? await askPin('Beacons back on: enter your PIN:')
        : await pinPrompt('Beacons back on: enter your PIN:', { autofill: false });
      if (!pin) return { cancelled: true };
      const r = await recordOpoChecklist(opoId, 'from_vendor', { pin, items });
      return r.ok ? { ok: true, data: r.body } : { ok: false, message: r.message };
    },
  });
  return { shown: true, recorded: !!(out && out.recorded) };
}

/** The pallets on a manifest, to find them (no record). */
export function showPallets(opoId, step = 'to_vendor') {
  return palletChecklistPrompt({ opoId, step });
}

/**
 * The Outsourcing staging board's Find (one pallet) / Find all (an order's pallets), before any manifest
 * exists: opens the pallet list and lights the beacons straight away (nothing is recorded).
 */
export function findPallets(palletIds, title) {
  const ids = [...new Set((palletIds || []).map(Number).filter(Boolean))];
  if (!ids.length) return Promise.resolve({ recorded: false });
  return palletChecklistPrompt({ palletIds: ids, step: 'find', title, autoFind: true });
}

/** The words for the send's checklist warnings ('' when none). */
export function sendWarningsText(data) {
  const w = data && data.pallet_checklist && Array.isArray(data.pallet_checklist.warnings)
    ? data.pallet_checklist.warnings : [];
  return w.length ? `Recorded, with ${w.length === 1 ? 'a note' : 'notes'}:\n${w.map((x) => `• ${x}`).join('\n')}` : '';
}
